export const TEAM_NAME_MIN_LENGTH = 3;
export const TEAM_NAME_MAX_LENGTH = 70;

const DISALLOWED_CONTROL_CHARS = /[\u0000-\u001F\u007F-\u009F\u200B-\u200D\uFEFF]/g;
const APOSTROPHE_VARIANTS = /[''\u2018\u2019\u2032\u0060\u00B4]/g;
const WHITESPACE_RUN = /\s+/g;

export const TEAM_NAME_REGEX =
  /^[A-Za-z0-9àâäæçéèêëîïôœöùûüÿñÀÂÄÆÇÉÈÊËÎÏÔŒÖÙÛÜŸÑ' ]+$/;

export type TeamNameValidationResult =
  | { ok: true; value: string }
  | { ok: false; message: string };

export const TEAM_NAME_INVALID_CHARACTERS_MESSAGE =
  "Le nom de l'équipe ne peut contenir que des lettres (accents acceptés), des chiffres, des apostrophes et des espaces.";

/**
 * Normalizes user input for display/editing: NFC, strips control chars,
 * unifies apostrophe variants, and removes characters outside the allowed set.
 */
export function sanitizeTeamNameInput(input: string): string {
  const normalized = input
    .normalize("NFC")
    .replace(DISALLOWED_CONTROL_CHARS, "")
    .replace(APOSTROPHE_VARIANTS, "'");

  const filtered = [...normalized]
    .filter((character) => /[A-Za-z0-9àâäæçéèêëîïôœöùûüÿñÀÂÄÆÇÉÈÊËÎÏÔŒÖÙÛÜŸÑ' ]/.test(character))
    .join("");

  return filtered.replace(WHITESPACE_RUN, " ");
}

/**
 * Parses and sanitizes a team name before persistence.
 * Rejects empty, too-short, too-long, or disallowed characters.
 */
export function parseTeamName(input: unknown): TeamNameValidationResult {
  if (typeof input !== "string") {
    return {
      ok: false,
      message: "Le nom de l'équipe est invalide.",
    };
  }

  const sanitized = sanitizeTeamNameInput(input).trim();

  if (!sanitized) {
    return {
      ok: false,
      message: "Le nom de l'équipe ne peut pas être vide.",
    };
  }

  if (sanitized.length < TEAM_NAME_MIN_LENGTH) {
    return {
      ok: false,
      message: `Le nom de l'équipe doit contenir au moins ${TEAM_NAME_MIN_LENGTH} caractères.`,
    };
  }

  if (sanitized.length > TEAM_NAME_MAX_LENGTH) {
    return {
      ok: false,
      message: `Le nom de l'équipe ne peut pas dépasser ${TEAM_NAME_MAX_LENGTH} caractères.`,
    };
  }

  if (!TEAM_NAME_REGEX.test(sanitized)) {
    return {
      ok: false,
      message: TEAM_NAME_INVALID_CHARACTERS_MESSAGE,
    };
  }

  return { ok: true, value: sanitized };
}

export function parseTeamNameOrThrow(input: unknown): string {
  const result = parseTeamName(input);
  if (!result.ok) {
    throw new Error(result.message);
  }
  return result.value;
}
